"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Braces } from "lucide-react";

/**
 * 3D-Bühne im Hintergrund + gestochen scharfe JSON-Kaskade im Vordergrund.
 */
export default function Preview() {
  const [depth, setDepth] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setDepth((d) => (d + 1) % 3), 1800);
    return () => clearInterval(id);
  }, []);

  const steps = [
    { key: "{ data }", x: -24 },
    { key: "orders[0]", x: 0 },
    { key: '"paid" ✓', x: 24, leaf: true },
  ];

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{ scale: [1, 1.22, 1], opacity: [0.45, 0.85, 0.45] }}
        transition={{ duration: 4.5, repeat: Infinity }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500/30 blur-3xl"
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(245,158,11,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(245,158,11,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <div className="relative z-10 flex flex-col items-center gap-2 antialiased">
        {steps.map((s, i) => {
          const isActive = i <= depth;
          return (
            <motion.div
              key={s.key}
              animate={{
                x: s.x,
                y: i === depth ? -2 : 0,
                opacity: isActive ? 1 : 0.4,
              }}
              transition={{ type: "spring", stiffness: 280, damping: 22 }}
              className={`flex items-center gap-2 rounded-xl border bg-white px-3.5 py-1.5 font-mono text-xs font-black dark:bg-zinc-900 ${
                s.leaf && isActive
                  ? "border-emerald-400/80 text-emerald-600 shadow-[0_14px_28px_-4px_rgba(16,185,129,0.55)] dark:text-emerald-400"
                  : isActive
                    ? "border-amber-400/70 text-amber-600 shadow-[0_12px_24px_-4px_rgba(245,158,11,0.45)] dark:text-amber-300"
                    : "border-black/10 text-neutral-400 dark:border-white/10"
              }`}
            >
              {i === 0 && <Braces className="h-3.5 w-3.5 text-amber-500" />}
              <span>{s.key}</span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
